import { ExpanderWithHeightTransition } from "components/ExpanderWithHeightTransition";
import {
  DeleteIconButton,
  MoveIconButton,
  ShowIconButton,
} from "components/ResumeForm/Form/IconButton";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  changeFormHeading,
  changeFormOrder,
  changeShowForm,
  selectHeadingByForm,
  selectIsFirstForm,
  selectIsLastForm,
  selectShowByForm,
  ShowForm,
  selectFormToHeading,
  setCurrentSection,
  selectCurrentSection,
  selectSectionTitles,
  toggleReset,
  setFormHeadingToDefault,
  selectSectionIndexes,
  selectSettings,
  changeLeftRightFormOrder,
} from "lib/redux/settingsSlice";
import {
  BuildingOfficeIcon,
  AcademicCapIcon,
  LightBulbIcon,
  WrenchIcon,
  PlusSmallIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import {
  addSectionInForm,
  deleteSectionInFormByIdx,
  moveSectionInForm,
  resetSection,
  selectResume,
} from "lib/redux/resumeSlice";
import { capitalizeFirstLetterOfEachWord } from "lib/utils/capitalizeFirstLetterOfEachWord";
import CustomIcon from "components/CustomIcon";
import { useEffect, useRef, useState } from "react";
import { useTemplateComponents } from "components/Resume/templateUtils";

const getCurrentSectionTitle = (
  sectionTitles: { id: string; title: string }[],
  currentSection: string
) => {
  const section = sectionTitles.find((title) => title.id === currentSection);
  return section ? section.title : "";
};

/**
 * BaseForm is the bare bone form, i.e. just the outline with no title and no control buttons.
 * ProfileForm uses this to compose its outline.
 */

export const BaseForm = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const currentSection = useAppSelector(selectCurrentSection);
  const { currentSectionIndex, previousSectionIndex } =
    useAppSelector(selectSectionIndexes);
  const dispatch = useAppDispatch();
  const handleSectionReset = () => {
    dispatch(toggleReset());
    dispatch(resetSection({ section: currentSection }));
  };

  const transition = `${
    currentSectionIndex > previousSectionIndex
      ? "fade-in-bottom"
      : "fade-in-top"
  }`;

  return (
    <section
      className={`flex flex-col gap-3 rounded-3xl bg-white p-3  pt-4 shadow transition-opacity duration-200 lg:p-3 xl:p-6 ${className} ${transition}`}
    >
      {currentSection === "personalDetails" ? (
        <div className="flex">
          <div className="block w-full border-b border-transparent text-xs font-semibold tracking-wide text-gray-900 outline-none hover:border-gray-300 hover:shadow-sm focus:border-gray-300 focus:shadow-sm lg:text-sm xl:text-lg">
            PERSONAL DETAILS
          </div>
          <button
            className="btn-gradient hidden items-center gap-2 rounded-[38px] lg:flex lg:px-2 lg:py-1 lg:text-xs xl:px-4 xl:py-2 xl:text-sm"
            onClick={handleSectionReset}
          >
            <div>
              <CustomIcon iconName="reset" stroke="white" />
            </div>
            Reset
          </button>
        </div>
      ) : null}
      {children}
    </section>
  );
};

const FORM_TO_ICON: { [section in ShowForm]: typeof BuildingOfficeIcon } = {
  workExperiences: BuildingOfficeIcon,
  educations: AcademicCapIcon,
  projects: LightBulbIcon,
  certifications: LightBulbIcon,
  languages: LightBulbIcon,
  awards: LightBulbIcon,
  skills: WrenchIcon,
  custom: WrenchIcon,
};

export const Form = ({
  form,
  addButtonText,
  children,
}: {
  form: ShowForm;
  addButtonText?: string;
  children: React.ReactNode;
}) => {
  const showForm = useAppSelector(selectShowByForm(form));
  const heading = useAppSelector(selectHeadingByForm(form));
  const formToHeading = useAppSelector(selectFormToHeading);
  const currentSection = useAppSelector(selectCurrentSection);
  const sectionTitles = useAppSelector(selectSectionTitles);
  const dispatch = useAppDispatch();
  const [isFocused, setIsFocused] = useState(false); // Track focus state
  const textRef = useRef<HTMLDivElement>(null);
  const headingInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // if (textRef.current) {
    //   textRef.current.style.width = `${textRef.current.scrollWidth}px`;
    // }
    if (textRef.current && headingInputRef.current) {
      const newWidth = textRef.current.offsetWidth;
      headingInputRef.current.style.width = `${newWidth + 2}px`;
    }
  }, [heading]);

  const makeHeadingEditable = () => {
    if (headingInputRef.current) {
      headingInputRef.current.focus();
    }
  };

  const handleSectionReset = () => {
    dispatch(toggleReset());
    dispatch(resetSection({ section: currentSection }));
    dispatch(setFormHeadingToDefault({ field: [currentSection as ShowForm] }));
  };

  const currentSectionTitle = getCurrentSectionTitle(
    sectionTitles,
    currentSection
  );

  // const dispatch = useAppDispatch();
  const setShowForm = (showForm: boolean) => {
    dispatch(changeShowForm({ field: form, value: showForm }));
  };
  const setHeading = (heading: string) => {
    dispatch(changeFormHeading({ field: form, value: heading }));
    dispatch(setCurrentSection(form));
  };

  const resume = useAppSelector(selectResume);
  const settings = useAppSelector(selectSettings);
  const { templateList, template } = useTemplateComponents(resume, settings);
  const selectedTemplate = templateList.find((t) => t.id === template);

  const isFirstForm = useAppSelector(
    selectIsFirstForm(form, selectedTemplate?.columns!)
  );
  const isLastForm = useAppSelector(
    selectIsLastForm(form, selectedTemplate?.columns!)
  );

  const column = selectedTemplate?.formsOrder?.left.includes(
    currentSection as ShowForm
  )
    ? "left"
    : "right";
  const handleMoveClick = (type: "up" | "down") => {
    if (selectedTemplate?.columns !== "double") {
      dispatch(changeFormOrder({ form, type }));
    } else {
      dispatch(changeLeftRightFormOrder({ form, column, direction: type }));
    }
  };

  const Icon = FORM_TO_ICON[form];

  return (
    <BaseForm
      className={`relative transition-opacity duration-200 ${
        showForm ? "pb-6" : "pb-2 opacity-60"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex grow items-center gap-2">
          {/* <Icon className="h-6 w-6 text-gray-600" aria-hidden="true" /> */}
          {/* Measure text width using a hidden span */}
          <span
            ref={textRef}
            className="invisible absolute whitespace-nowrap font-semibold tracking-wide lg:text-sm xl:text-lg"
          >
            {heading || "Heading"} {/* Placeholder for measuring */}
          </span>

          <input
            ref={headingInputRef}
            type="text"
            className="block border-b border-transparent font-semibold tracking-wide text-gray-900 outline-none hover:border-gray-300 hover:shadow-sm focus:border-gray-300 focus:shadow-sm lg:text-sm xl:text-lg"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            onFocus={() => setIsFocused(true)} // Hide pencil on focus
            onBlur={() => setIsFocused(false)} // Show pencil on blur
            style={{ width: `${textRef.current?.offsetWidth || 0}px` }} // Dynamically adjust width
          />
          {
            // Show pencil icon only when not focused
            !isFocused && (
              <PencilSquareIcon
                className="h-5 w-5 cursor-pointer text-gray-600"
                onClick={makeHeadingEditable}
              />
            )
          }
          <div className="ml-auto flex gap-2">
            {currentSection !== "resumeSettings" ? (
              <button
                className="btn-gradient hidden items-center gap-2 rounded-[38px] lg:flex lg:px-2 lg:py-1 lg:text-xs xl:px-4 xl:py-2 xl:text-sm"
                onClick={handleSectionReset}
              >
                <div>
                  <CustomIcon iconName="reset" stroke="white" />
                </div>
                Reset
              </button>
            ) : null}
            <div className="flex items-center gap-1.5 rounded-3xl border-2 border-[rgba(46,155,255,19%)] bg-[#F0F0F0] px-1.5">
              {!isFirstForm && (
                <MoveIconButton
                  type="up"
                  onClick={handleMoveClick}
                  size="small"
                />
              )}
              {!isLastForm && (
                <MoveIconButton
                  type="down"
                  onClick={handleMoveClick}
                  size="small"
                />
              )}
              <ShowIconButton
                show={showForm}
                setShow={setShowForm}
                size="small"
              />
            </div>
          </div>
        </div>
      </div>
      <ExpanderWithHeightTransition expanded={showForm} overflowHidden={false}>
        {children}
      </ExpanderWithHeightTransition>
      {showForm && addButtonText && (
        <div className="mt-2 flex justify-start">
          <button
            type="button"
            onClick={() => {
              dispatch(addSectionInForm({ form }));
            }}
            className="flex items-center rounded-3xl border-[1px] border-[#1689F3] bg-white pl-3 pr-4 font-semibold text-[#1689F3] shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 lg:py-1 lg:text-xs xl:py-2 xl:text-sm"
          >
            <PlusSmallIcon
              className="-ml-0.5 mr-1.5 h-5 w-5 text-[#1689F3]"
              aria-hidden="true"
            />
            {addButtonText}
          </button>
        </div>
      )}
    </BaseForm>
  );
};

export const FormSection = ({
  form,
  idx,
  showMoveUp,
  showMoveDown,
  showDelete,
  deleteButtonTooltipText,
  children,
}: {
  form: ShowForm;
  idx: number;
  showMoveUp: boolean;
  showMoveDown: boolean;
  showDelete: boolean;
  deleteButtonTooltipText: string;
  children: React.ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const handleDeleteClick = () => {
    dispatch(deleteSectionInFormByIdx({ form, idx }));
  };
  const handleMoveClick = (direction: "up" | "down") => {
    dispatch(moveSectionInForm({ form, direction, idx }));
  };

  return (
    <>
      {idx !== 0 && (
        <div className="mb-4 mt-6 border-t-2 border-dotted border-gray-200" />
      )}
      <div className="relative grid grid-cols-6 gap-3">
        {children}
        <div className={`absolute right-0 top-0 flex gap-0.5 `}>
          <div
            className={`transition-all duration-300 ${
              showMoveUp ? "" : "invisible opacity-0"
            } ${showMoveDown ? "" : "-mr-6"}`}
          >
            <MoveIconButton
              type="up"
              size="small"
              onClick={() => handleMoveClick("up")}
            />
          </div>
          <div
            className={`transition-all duration-300 ${
              showMoveDown ? "" : "invisible opacity-0"
            }`}
          >
            <MoveIconButton
              type="down"
              size="small"
              onClick={() => handleMoveClick("down")}
            />
          </div>
          <div
            className={`transition-all duration-300 ${
              showDelete ? "" : "invisible opacity-0"
            }`}
          >
            <DeleteIconButton
              onClick={handleDeleteClick}
              tooltipText={deleteButtonTooltipText}
            />
          </div>
        </div>
      </div>
    </>
  );
};
